/* Copyright (c) 2022-2023, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { PluginHandlers, PluginResponse } from './plugin.models';

/**
 * An abstract base class for implementation of a bridge command.
 */
export abstract class Plugin {
    /**
     * Get metadata of what bridge command module/methods are handled by the plugins.
     */
    public abstract get handlers(): PluginHandlers;

    /**
     * Creates a `PluginResponse` success object
     *
     * This is a private method and WILL NOT BE accessible by concrete plugin implementations.
     */
    private createSuccessResponse(data: string): PluginResponse {
        return {
            success: true,
            code: 200,
            data
        };
    }

    /**
     * Creates a PluginResponse error object
     *
     * This is a private method and WILL NOT BE accessible by concrete plugin implementations.
     */
    private createErrorResponse(code: number, message: string): PluginResponse {
        return {
            success: false,
            code,
            error: message
        };
    }

    /**
     * Create and return an `Observable` to `PluginResponse` containing an error.
     *
     * @param code - Bridge command's error code
     * @param message - Any message associated with the error
     */
    protected makeError(code: number, message: string): Observable<PluginResponse> {
        return throwError(this.createErrorResponse(code, message));
    }

    /**
     * Create and return an `Observable` to `PluginResponse` which will emit a value.
     *
     * @param data - Bridge command's response
     */
    protected makeResult(data: string): Observable<PluginResponse> {
        return of(this.createSuccessResponse(data));
    }

    /**
     * Return an `Observable` to `PluginResponse` which will emit several values.
     *
     * @param obs$ - Source observable which will emit bridge command result
     */
    protected makePersistentResult(obs$: Observable<string>): Observable<PluginResponse> {
        return obs$.pipe(map((data: string) => this.createSuccessResponse(data)));
    }
}
